import React, {
  FunctionComponent,
  SyntheticEvent,
  useEffect,
  useState,
} from 'react';
import { format } from 'date-fns';
import { useForm, FormProvider } from 'react-hook-form';
import { Container, Grid, Header } from 'semantic-ui-react';
import { useMutation, useQuery } from '@apollo/react-hooks';
import withStyles from 'react-jss';
import classnames from 'classnames';
import { pathOr, omit } from 'ramda';

import { ClassProp } from 'helpers/types';
import { Profile } from 'components/UserMgmt/UserMgmt.helpers';
import FormSelectList from 'components/FormComponents/FormSelect';
import FormDateInput from 'components/FormComponents/FormDateInput';
import FormInput from 'components/FormComponents/FormInput';
import FormTimeInput from 'components/FormComponents/FormTimeInput';
import { GET_PROFILE } from 'components/UserMgmt/UserMgmt.queries';
import DisplayTimeStamp from 'scenes/Home/scenes/MerchantStatusDetails/scenes/sections/MerchantOffers/components/DisplayTimestamp';
import { useGetMarketplaces } from 'helpers/useGetMarketplaces';

import {
  getMarketingFee,
  FinancialTermUnitTypes,
  buildFinancialTerms,
  filterTargetingRules,
  handleSuccess,
  handleError,
} from 'scenes/Home/scenes/MerchantStatusDetails/scenes/sections/MerchantOffers/MerchantOffer.helpers';
import {
  buildTimeStamp,
  cstToUTC,
  dateIsAfterCurrentDate,
  checkDates,
  DATE_FORMAT,
  oneMinuteToMidnight,
  getCurrentCSTDateWithTime,
} from 'helpers/date';
import {
  Segment,
  TermUnits,
  ReplaceSegmentFormData,
} from 'scenes/Home/scenes/MerchantStatusDetails/scenes/sections/MerchantOffers/MerchantOffer.types';
import {
  REPLACE_OFFER_SEGMENT,
  GET_CAMPAIGN_BY_UUID,
  GET_OFFER,
} from 'scenes/Home/scenes/MerchantStatusDetails/scenes/sections/MerchantOffers/scenes/PulseOffer/PulseOffer.queries';

import SelectAudience, {
  AudienceSelectEvent,
  AttachedAudiences,
} from '../SelectAudience';

import styles from './ReplaceSegment.styles';

type Styles = ClassProp<ReturnType<typeof styles>>;

type Props = {
  classes: Styles;
  segment: Segment;
  offerUuid: string;
  merchantId: string;
  cancelUpdate: () => void;
  attachedAudiences: AttachedAudiences;
};

interface UserProfileData {
  userProfile: Profile;
}

const defaultSuccessMessage = [
  {
    displayMessage: 'Success!',
    message: 'Offer Segment Replaced Successfully.',
  },
];
const ReplaceSegment: FunctionComponent<Props> = ({
  segment,
  offerUuid,
  merchantId,
  cancelUpdate,
  classes,
  attachedAudiences,
}) => {
  const [insertionOrder, setInsertionOrder] = useState<string>('');
  const [audienceError, setAudienceError] = useState<string | null>(null);
  const [usePercentages, setUsePercentages] = useState<boolean>(
    !segment.cashbackAmount,
  );
  const [userName, setUserName] = useState<string | null>(null);

  const currentAudienceUuid = pathOr(
    null,
    ['audience', 'audienceUuid'],
    segment,
  );
  const financialTermItems = pathOr(
    '',
    ['financialTerm', 'financialTermItems'],
    segment,
  );
  const campaignUuid = pathOr('', [0, 'campaignId'], financialTermItems);
  const marketingFee = getMarketingFee(financialTermItems) as string;
  const defaultReplaceDate = new Date(segment.startDate);
  const defaultEndDate = new Date(segment.endDate || '12/31/2035');
  const upperCasedMarketplaces = segment.marketplaces.map((marketplace) =>
    marketplace.toUpperCase(),
  );
  const {
    MarketplaceOptions,
    buildMarketplaces,
    loading: loadingMarketPlaces,
  } = useGetMarketplaces();
  const methods = useForm<ReplaceSegmentFormData>({
    mode: 'onBlur',
    defaultValues: {
      marketingFee,
      audience: currentAudienceUuid,
      marketplaces: upperCasedMarketplaces,
      cashbackDailyLimit: segment.cashbackDailyLimit,
      cashback: segment.cashbackAmount || segment.cashbackPercentage,
      ongoingEndDate: !segment.endDate,
      replaceDate: defaultReplaceDate,
      replaceTime: format(oneMinuteToMidnight(), 'HH:mm'),
      endDate: defaultEndDate,
      endTime: format(defaultEndDate, 'HH:mm'),
    },
  });

  const {
    register,
    handleSubmit,
    getValues,
    watch,
    setError,
    clearErrors,
    trigger,
  } = methods;

  const ongoingEndDate = watch('ongoingEndDate');
  const replaceTime = watch('replaceTime');
  const endTime = watch('endTime');

  useEffect(() => {
    trigger();
  }, [replaceTime]);

  useEffect(() => {
    trigger();
  }, [endTime]);

  const CampaignResponse = useQuery(GET_CAMPAIGN_BY_UUID, {
    variables: { params: { campaignUuid } },
  });
  const { data, loading } = CampaignResponse;

  const handleReplaceSegmentResponse = () => {
    handleSuccess(defaultSuccessMessage);
    cancelUpdate();
  };

  const [runMutation, { loading: isSubmitting }] = useMutation(
    REPLACE_OFFER_SEGMENT,
    {
      onCompleted: handleReplaceSegmentResponse,
      onError: handleError,
      awaitRefetchQueries: true,
      refetchQueries: [
        {
          query: GET_OFFER,
          variables: {
            params: {
              offerUuid,
              hydrateFinancialTerms: true,
            },
          },
        },
      ],
    },
  );

  const { data: userData, loading: profileLoading } = useQuery<UserProfileData>(
    GET_PROFILE,
  );

  useEffect(() => {
    register({ name: 'marketplaces' });
    register({ name: 'audience' });
    register(
      { name: 'replaceDate' },
      {
        required: 'Replace Date is required',
        validate: {
          oneOf: () =>
            checkDates(getValues().replaceDate, getValues().endDate) ||
            'Replace Date must come before End Date',
          isAfter: (value) =>
            dateIsAfterCurrentDate(
              getCurrentCSTDateWithTime(value, getValues().replaceTime),
            ) || "Start Date can't be earlier than today",
        },
      },
    );
    register(
      { name: 'endDate' },
      {
        required: 'End Date is required',
        validate: {
          oneOf: () =>
            checkDates(
              getCurrentCSTDateWithTime(
                getValues().replaceDate,
                getValues().replaceTime,
              ),
              getCurrentCSTDateWithTime(
                getValues().endDate,
                getValues().endTime,
              ),
            ) || 'End Date must come after Replace Date',
          isAfter: (value) =>
            dateIsAfterCurrentDate(value) ||
            "End Date can't be earlier than today",
        },
      },
    );
    register({ name: 'replaceTime' }, { required: 'Replace Time is required' });
    register({ name: 'endTime' }, { required: 'End Time is required' });
  }, [register]);

  useEffect(() => {
    if (loading) return;

    const ticket = pathOr(
      '',
      ['campaignByUuid', 'insertionOrder', 'ticket'],
      data,
    );
    setInsertionOrder(ticket);
  }, [data, loading]);

  useEffect(() => {
    if (profileLoading || !userData) return;
    const {
      userProfile: { givenName, familyName },
    } = userData;
    setUserName(`${givenName} ${familyName}`);
  }, [userData, profileLoading]);

  useEffect(() => {
    if (audienceError) {
      setError('audience', {
        message: audienceError,
      });
    } else {
      clearErrors('audience');
    }
  }, [audienceError]);

  const handleClick = (_: SyntheticEvent<HTMLElement, Event>) => cancelUpdate();

  const changeFinancialUnit = (termUnit: TermUnits) => {
    setUsePercentages(termUnit === TermUnits.PERCENTAGE);
  };

  const handleAudienceChange = ({
    audienceId,
    audienceDates = [],
  }: AudienceSelectEvent) => {
    if (!audienceId || audienceId === currentAudienceUuid) {
      setAudienceError(null);
      return;
    }
    const startDate = getValues().replaceDate as Date;
    const endDate = getValues().endDate as Date;
    if (!startDate || !endDate) {
      setAudienceError('start date and end date must be set first');
      return;
    }
    let error = null;

    audienceDates.forEach((dateRange) => {
      const segmentStartDate = new Date(dateRange[0]);
      const segmentEndDate = new Date(dateRange[1]);
      if (startDate < segmentEndDate && endDate > segmentStartDate) {
        error = 'Audience is already used during these dates';
      }
    });

    setAudienceError(error);
  };

  const onSubmit = ({
    replaceDate,
    replaceTime,
    endDate,
    endTime,
    ongoingEndDate,
    cashback,
    marketingFee,
    cashbackDailyLimit,
    marketplaces,
    insertionOrderRef,
    offerUuid,
    segmentUuid,
    audience,
  }: ReplaceSegmentFormData) => {
    if (audienceError) {
      setError('audience', {
        message: audienceError,
      });
      return;
    }
    const newCashbackPercentage = usePercentages
      ? parseFloat(cashback as string)
      : null;
    const newCashbackAmount = !usePercentages
      ? parseFloat(cashback as string)
      : null;
    const newMarketingFee = parseFloat(marketingFee as string);

    const newFinancialTermItems = buildFinancialTerms(
      financialTermItems,
      newCashbackPercentage as number,
      newCashbackAmount as number,
      newMarketingFee,
    );

    const targetingRules = filterTargetingRules({ segment, audience });

    const replaceTimeStamp = cstToUTC(
      buildTimeStamp(format(replaceDate, DATE_FORMAT), replaceTime),
    );
    const endTimeStamp = ongoingEndDate
      ? ''
      : cstToUTC(buildTimeStamp(format(endDate, DATE_FORMAT), endTime));

    const formattedSegment = {
      offerUuid,
      segmentUuid,
      insertionOrderRef,
      targetingRules,
      marketplaces: buildMarketplaces(marketplaces),
      segmentPriority: segment.segmentPriority,
      hideOfferForSegment: segment.hideOfferForSegment,
      segmentType: segment.segmentType,
      promotionType: segment.promotionType,
      offerTitleOverride: segment.offerTitleOverride,
      financialTerm: {
        financialTermItems: newFinancialTermItems,
      },
      startDate: replaceTimeStamp,
      endDate: endTimeStamp,
      cashbackPercentage: newCashbackPercentage,
      cashbackAmount: newCashbackAmount,
      cashbackDailyLimit: parseFloat(cashbackDailyLimit as string),
      updatedBy: userName,
      dryRun: false,
      overrideCashbackCheck: false,
    };

    const omitFields = [
      'uuid',
      'effectiveAt',
      'exactSpendToActivate',
      'minSpendToActivate',
      'marketingFee',
      '__typename',
      'throttle',
      'audience',
    ];

    omitFields.push(usePercentages ? 'cashbackAmount' : 'cashbackPercentage');
    if (ongoingEndDate) {
      omitFields.push('endDate');
    }
    if (!segment.offerTitleOverride || segment.offerTitleOverride.length < 1) {
      omitFields.push('offerTitleOverride');
    }

    const replaceSegment = omit(omitFields, formattedSegment);

    runMutation({
      variables: {
        params: { ...replaceSegment },
      },
    });

    return;
  };

  return (
    <Container className={classes.container}>
      <Header as="h2">Replace Segment</Header>
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <input
            type="hidden"
            value={offerUuid}
            name="offerUuid"
            ref={register}
          />
          <input
            type="hidden"
            value={segment.uuid}
            name="segmentUuid"
            ref={register}
          />
          <input
            type="hidden"
            value={insertionOrder}
            name="insertionOrderRef"
            ref={register}
          />
          <Grid>
            <Grid.Row>
              <Grid.Column>
                <DisplayTimeStamp
                  label="Original Start Date:"
                  date={segment.startDate}
                />
              </Grid.Column>
            </Grid.Row>
            <Grid.Row>
              <Grid.Column width={4}>
                <FormInput
                  name="ongoingEndDate"
                  type="checkbox"
                  placeholder="On Going End Date?"
                  label="On Going End Date?"
                  tabIndex={0}
                  register={register}
                />
              </Grid.Column>
              {!ongoingEndDate && (
                <>
                  <Grid.Column width={4}>
                    <FormDateInput
                      name="endDate"
                      label="End Date"
                      tabIndex={1}
                      placeholder={defaultEndDate}
                    />
                  </Grid.Column>
                  <Grid.Column width={4}>
                    <FormTimeInput
                      name="endTime"
                      label="End Time (CST)"
                      tabIndex={2}
                      placeholder={defaultEndDate}
                    />
                  </Grid.Column>
                </>
              )}
            </Grid.Row>
            <Grid.Row>
              <Grid.Column width={4}>
                <FormDateInput
                  name="replaceDate"
                  label="Date for replacement"
                  tabIndex={3}
                  placeholder={defaultReplaceDate}
                />
              </Grid.Column>
              <Grid.Column width={4}>
                <FormTimeInput
                  name="replaceTime"
                  label="Time of replacement (CST)"
                  tabIndex={4}
                  placeholder={defaultReplaceDate}
                />
              </Grid.Column>
            </Grid.Row>
            <Grid.Row>
              <Grid.Column width={4}>
                <FormSelectList
                  label="Financial Term Unit"
                  name="financialTermUnit"
                  tabIndex={10}
                  dropDownOptions={FinancialTermUnitTypes}
                  defaultValue={
                    usePercentages ? TermUnits.PERCENTAGE : TermUnits.USD
                  }
                  onSelectChange={changeFinancialUnit}
                />
              </Grid.Column>
              <Grid.Column width={4}>
                <FormInput
                  name="cashback"
                  type="number"
                  placeholder="Enter Cash Back"
                  label={`Cash Back (${usePercentages ? '%' : '$'})`}
                  tabIndex={13}
                  register={register}
                />
              </Grid.Column>
              <Grid.Column width={4}>
                <FormInput
                  name="cashbackDailyLimit"
                  type="number"
                  placeholder="Enter a Daily Cashback Limit"
                  label="Daily Cashback Limit ($)"
                  tabIndex={11}
                  register={register}
                />
              </Grid.Column>
              <Grid.Column width={4}>
                <FormInput
                  name="marketingFee"
                  type="number"
                  placeholder="Enter Marketing Fee for Campaign"
                  label={`Marketing Fee (${usePercentages ? '%' : '$'})`}
                  tabIndex={12}
                  register={register}
                />
              </Grid.Column>
            </Grid.Row>

            <Grid.Row className={classes.sectionHeader}>
              <hr className={classes.divider} />
              <h3>Visibility</h3>
            </Grid.Row>

            <Grid.Row className={classes.formRow}>
              <Grid.Column width={8}>
                <FormSelectList
                  label="Marketplaces"
                  name="marketplaces"
                  tabIndex={6}
                  dropDownOptions={MarketplaceOptions}
                  defaultValue={upperCasedMarketplaces}
                  multiple
                  wrapSelection
                  loading={loadingMarketPlaces}
                />
              </Grid.Column>
            </Grid.Row>

            <Grid.Row className={classes.formRow}>
              <Grid.Column width={8}>
                <SelectAudience
                  merchantId={merchantId}
                  onSelectChange={handleAudienceChange}
                  attachedAudiences={attachedAudiences}
                  currentAudienceUuid={currentAudienceUuid}
                />
              </Grid.Column>
            </Grid.Row>
            <Grid.Row className={classes.formRow}>
              <button
                className={classnames(
                  classes.button,
                  classes.buttonSecondary,
                  classes.mediumBtn,
                )}
                onClick={handleClick}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className={classnames(classes.button, classes.mediumBtn)}
              >
                {isSubmitting ? '...Submitting' : 'Replace Offer Segment'}
              </button>
            </Grid.Row>
          </Grid>
        </form>
      </FormProvider>
    </Container>
  );
};

export default withStyles(styles)(ReplaceSegment);
